import { useCallback, useEffect, useState } from 'react';
import type { FichaPago } from '../../../api/ops/pagos';
import type { Reserva } from '../../../api/ops/reservas';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Campo } from '../../../componentes/Campo';
import { Chip } from '../../../componentes/Chip';
import { Deshacer } from '../../../componentes/Deshacer';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { Interruptor } from '../../../componentes/Interruptor';
import { Ventana } from '../../../componentes/Ventana';
import { rolesDe } from '../../../logica/acceso';
import { dinero } from '../../../logica/dinero';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../../logica/estados';
import { useCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { dondeEs, nombreDe, nombreZona, useNombres, type ContextoReservas } from '../contexto';
import { cuandoFue, diaAbreviado, diaRelativo, etiquetaDia, hoyEn, horaEn } from '../fechas';
import { AYUDA, ESTADO, INTENTOS_COBRO, PERMISOS, conArticulo, conPalabra } from '../textos';

/**
 * 8.4 Por completar: las reservas que terminaron sin llegada (`status=pending_completion`), por día, con «Vino» y «No
 * vino» grandes (G14: nada se completa solo; G15: el no-show lo marca el personal). «Vino» y «No vino» sin cobro salen a
 * los 5 s con «Deshacer» (G159); «No vino» con importe que cobrar (`no_show_charge_amount`, lo calcula la API, G842) abre
 * la ventana con el importe, que deja no cobrarlo (queda en Auditoría).
 * Debajo, «Cobros de no-show sin cobrar» (`listPayments` con `concept=no_show_fee` y `status=rejected`; el intento de
 * `getPayment`) con «Reintentar el cobro» hasta el tercer intento (G318), y «No presentadas estos días» para deshacer un
 * no-show con motivo: se pinta el estado que devuelve la API (G792).
 */
interface Props extends PropsPantalla {
    contexto: ContextoReservas;
}

interface Fallido {
    pago: FichaPago;
    reserva: Reserva | null;
    intento: number;
    motivo: string | null;
}

interface Datos {
    pendientes: Reserva[];
    fallidos: Fallido[];
    noPresentadas: Reserva[];
}

type Ventanas =
    | { tipo: 'no-vino'; r: Reserva; cobrar: boolean }
    | { tipo: 'reintentar'; f: Fallido }
    | { tipo: 'deshacer'; r: Reserva; fallido: boolean; terminada: boolean; motivo: string; probado: boolean };

const MOTIVO_MIN = 10;

/** Lo que se cobraría al marcar el «no vino» (G842): lo da la API; el panel no lo calcula. */
function importeNoShow(r: Reserva): { amount: number; currency: string } | null {
    const i = r.no_show_charge_amount;
    return i != null && i.amount > 0 ? i : null;
}

async function paginas(pedir: (cursor: string | null) => Promise<{ data: Reserva[]; next_cursor?: string | null }>): Promise<Reserva[]> {
    const lista: Reserva[] = [];
    let cursor: string | null = null;
    for (let i = 0; i < 10; i += 1) {
        const p = await pedir(cursor);
        lista.push(...p.data);
        cursor = p.next_cursor ?? null;
        if (cursor === null) {
            break;
        }
    }
    return lista;
}

export function PorCompletar(props: Props) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar, ruta, contexto } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const [datos, setDatos] = useState<Datos | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [hechos, setHechos] = useState<Readonly<Record<string, 'completed' | 'no_show'>>>({});
    const [ventana, setVentana] = useState<Ventanas | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const puedeCompletar = permisos.puede(PERMISOS.completar) && !soloLectura;
    const puedeDeshacer = permisos.puede(PERMISOS.deshacerNoShow);
    const verPagos = permisos.puede(PERMISOS.pagosVer);
    const puedeCobrar = permisos.puede(PERMISOS.cobrar);
    const hoy = hoyEn(contexto.zona);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        let vivo = true;
        const ahora = new Date();
        const pendientes = paginas((cursor) => ops.listReservations(negocio, { status: 'pending_completion', sort: 'starts_at', limit: 100, cursor }));
        const fallidos: Promise<Fallido[]> = verPagos
            ? ops
                  .listPayments(negocio, { concept: 'no_show_fee', status: ['rejected'], payable_type: 'reserva', limit: 50 })
                  .then((l) =>
                      Promise.all(
                          l.data.map(async (p) => {
                              const [pago, reserva] = await Promise.all([ops.getPayment(negocio, p.id).catch(() => p as FichaPago), ops.getReservation(negocio, p.payable.id).then((x) => x.datos).catch(() => null)]);
                              const ultimo = [...(pago.attempts ?? [])].sort((a, b) => b.attempt_number - a.attempt_number)[0];
                              return { pago, reserva, intento: ultimo?.attempt_number ?? 1, motivo: ultimo?.decline_label ?? null };
                          }),
                      ),
                  )
                  .catch(() => [])
            : Promise.resolve([]);
        const noPresentadas = puedeDeshacer
            ? paginas((cursor) => ops.listReservations(negocio, { status: 'no_show', from: new Date(ahora.getTime() - 7 * 86_400_000).toISOString(), to: ahora.toISOString(), sort: '-starts_at', limit: 100, cursor })).catch(() => [])
            : Promise.resolve([]);
        Promise.all([pendientes, fallidos, noPresentadas])
            .then(([p, f, n]) => {
                const conFallo = new Set(f.map((x) => x.pago.payable.id));
                if (vivo) {
                    setDatos({ pendientes: p, fallidos: f, noPresentadas: n.filter((r) => !conFallo.has(r.id)) });
                    setHechos({});
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, verPagos, puedeDeshacer, intento]);

    const ids = [
        ...(datos?.pendientes ?? []).map((r) => r.customer_id),
        ...(datos?.noPresentadas ?? []).map((r) => r.customer_id),
        ...(datos?.fallidos ?? []).map((f) => f.pago.customer_id ?? f.reserva?.customer_id),
    ].filter((x): x is string => typeof x === 'string');
    const nombres = useNombres(ops, negocio, ids, permisos.puede(PERMISOS.clientes));

    useCabecera({ ayuda: conPalabra(AYUDA.pendientes, contexto.palabra), acciones: [] });

    if (error !== null && datos === null) {
        const e = estadoDeError(error);
        return e.tipo === 'sin-permiso' ? (
            <EstadoSinPermiso seccion="Reservas" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />
        ) : (
            <EstadoError seccion="las reservas" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />
        );
    }
    if (datos === null) {
        return <EstadoCargando seccion="las reservas" />;
    }

    const base = ruta.split('?')[0] ?? ruta;
    const abrir = (id: string) => {
        navegar(`${base}?apartado=por-completar&reserva=${encodeURIComponent(id)}`);
    };
    const fallar = (e: unknown) => {
        avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido guardar. Prueba de nuevo.' });
    };

    const deUnToque = (r: Reserva, nombre: string, vino: boolean) => {
        setHechos((x) => ({ ...x, [r.id]: vino ? 'completed' : 'no_show' }));
        envio.programar({
            texto: vino ? `${nombre} vino.` : `${nombre} no vino.`,
            donde: r.id,
            enviar: () => {
                ops.completePendingReservation(negocio, r.id, vino ? { attended: true } : { attended: false, apply_charge: false }).then(recargar).catch((e: unknown) => {
                    fallar(e);
                    recargar();
                });
            },
            alDeshacer: () => {
                setHechos((x) => Object.fromEntries(Object.entries(x).filter(([k]) => k !== r.id)));
            },
        });
    };

    const noVino = (r: Reserva, nombre: string) => {
        if (importeNoShow(r) !== null) {
            setVentana({ tipo: 'no-vino', r, cobrar: true });
        } else {
            deUnToque(r, nombre, false);
        }
    };

    const dias = [...new Set(datos.pendientes.map((r) => r.service_date))].sort();
    const nombreDeFallido = (f: Fallido) => {
        const cliente = f.pago.customer_id ?? f.reserva?.customer_id ?? null;
        return nombreDe({ customer_id: cliente }, nombres);
    };

    const ejecutar = (accion: Promise<unknown>, alAcabar: (x: unknown) => void) => {
        setOcupado(true);
        accion
            .then((x) => {
                setVentana(null);
                alAcabar(x);
                recargar();
            })
            .catch(fallar)
            .finally(() => {
                setOcupado(false);
            });
    };

    const confirmarVentana = () => {
        if (ventana === null) {
            return;
        }
        if (ventana.tipo === 'no-vino') {
            const nombre = nombreDe(ventana.r, nombres);
            ejecutar(ops.completePendingReservation(negocio, ventana.r.id, { attended: false, apply_charge: ventana.cobrar }), () => {
                avisar({ tipo: 'ok', texto: `${nombre} no vino.` });
            });
            return;
        }
        if (ventana.tipo === 'reintentar') {
            ejecutar(ops.retryOffSessionCharge(negocio, ventana.f.pago.id), (x) => {
                const p = x as FichaPago;
                const ultimo = [...(p.attempts ?? [])].sort((a, b) => b.attempt_number - a.attempt_number)[0];
                avisar(ultimo?.result === 'succeeded' || p.status === 'completed' ? { tipo: 'ok', texto: `Cobrado: ${dinero(p.amount)}.` } : { tipo: 'error', texto: ultimo?.decline_label ?? 'El banco lo ha rechazado.' });
            });
            return;
        }
        if (ventana.motivo.trim().length < MOTIVO_MIN) {
            setVentana({ ...ventana, probado: true });
            return;
        }
        const nombre = nombreDe(ventana.r, nombres);
        ejecutar(ops.undoReservationNoShow(negocio, ventana.r.id, ventana.motivo.trim()), (x) => {
            // G792: el estado que devuelve la API (confirmada, o por completar si su franja ya terminó).
            const r = x as Reserva;
            avisar({ tipo: 'ok', texto: r.status === 'pending_completion' ? `${nombre} queda por completar.` : `${nombre}: ${ESTADO[r.status][0].toLocaleLowerCase('es')}.` });
        });
    };

    const textoVentana = (() => {
        if (ventana === null) {
            return { titulo: '', texto: '', accion: '', icono: 'check' as const, ocupadoTexto: '' };
        }
        if (ventana.tipo === 'no-vino') {
            const libera = `Se libera ${conArticulo(contexto.palabra)} para todos los canales.`;
            return {
                titulo: `${nombreDe(ventana.r, nombres)} no vino`,
                texto: `${libera} ${ventana.cobrar ? `Se le cobran ${dinero(importeNoShow(ventana.r))} en su tarjeta de garantía; le avisamos antes del cobro.` : 'No se le cobrará el no-show.'}`,
                accion: 'Marcar no presentada',
                icono: 'user-x' as const,
                ocupadoTexto: 'Marcando…',
            };
        }
        if (ventana.tipo === 'reintentar') {
            return {
                titulo: `Reintentar el cobro de ${dinero(ventana.f.pago.amount)}`,
                texto: `Intento ${String(ventana.f.intento + 1)} de ${String(INTENTOS_COBRO)} sobre la tarjeta de garantía de ${nombreDeFallido(ventana.f)}. Solo cuenta como cobrado si el banco lo confirma.`,
                accion: 'Reintentar',
                icono: 'refresh-cw' as const,
                ocupadoTexto: 'Cobrando…',
            };
        }
        const { terminada } = ventana;
        return {
            titulo: `Deshacer el no-show de ${nombreDe(ventana.r, nombres)}`,
            texto: `Vuelve a confirmada${ventana.fallido ? ' y se anula el cobro pendiente' : ''}. ${terminada ? 'Como su franja ya terminó, quedará por completar.' : ''}`.trim(),
            accion: 'Deshacer el no-show',
            icono: 'undo-2' as const,
            ocupadoTexto: 'Deshaciendo…',
        };
    })();

    return (
        <div className="rv-rs-apartado rv-rs-apartado--ancho">
            {dias.map((d) => {
                const delDia = datos.pendientes.filter((r) => r.service_date === d);
                const quedan = delDia.filter((r) => hechos[r.id] === undefined).length;
                return (
                    <section key={d} className="rv-rs-grupo" aria-label={diaRelativo(d, hoy)}>
                        <div className="rv-rs-grupo__cab">
                            <span className="rv-rs-ceja">{diaRelativo(d, hoy)}</span>
                            <span>{quedan > 0 ? `${String(quedan)} por completar` : 'Hecho'}</span>
                        </div>
                        {delDia.map((r) => {
                            const nombre = nombreDe(r, nombres);
                            const hecho = hechos[r.id];
                            const pendiente = envio.pendiente?.donde === r.id;
                            return (
                                <div key={r.id} className={`rv-rs-pend${movil ? ' rv-rs-pend--movil' : ''}`}>
                                    <button type="button" className="rv-rs-fila__abrir" aria-label={`Abrir la reserva de ${nombre}`} style={{ opacity: hecho === undefined ? 1 : 0.6 }} onClick={() => { abrir(r.id); }}>
                                        <span className="rv-rs-baldosa"><strong>{r.local_time ?? horaEn(r.starts_at, contexto.zona)}</strong><small>{etiquetaDia(r.service_date, hoy)}</small></span>
                                        <span className="rv-rs-fila__texto">
                                            <strong>{nombre}</strong>
                                            <span>{`${String(r.party_size)} personas · ${dondeEs(contexto, r)}`}</span>
                                        </span>
                                    </button>
                                    {hecho === undefined && puedeCompletar && (
                                        <div className="rv-rs-pend__botones">
                                            <button type="button" className="rv-b rv-b-sec rv-rs-grande" aria-label={`${nombre} vino`} onClick={() => { deUnToque(r, nombre, true); }}>
                                                <Icono nombre="check" />
                                                <span>Vino</span>
                                            </button>
                                            <button type="button" className="rv-b rv-b-sec rv-b-rojo rv-rs-grande" aria-label={`${nombre} no vino`} onClick={() => { noVino(r, nombre); }}>
                                                <Icono nombre="x" />
                                                <span>No vino</span>
                                            </button>
                                        </div>
                                    )}
                                    {hecho !== undefined && (
                                        <div className="rv-rs-pend__hecho">
                                            <Chip estado={ESTADO[hecho][1]}>{ESTADO[hecho][0]}</Chip>
                                            {pendiente && envio.pendiente !== null && <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} ancho={movil ? '100%' : 'auto'} />}
                                        </div>
                                    )}
                                    {hecho === undefined && !puedeCompletar && <span className="rv-rs-pend__bloqueo">{soloLectura ? 'Solo lectura' : 'Lo completa el equipo'}</span>}
                                </div>
                            );
                        })}
                    </section>
                );
            })}

            {datos.pendientes.length === 0 && (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa rv-rs-baldosa-negra"><Icono nombre="check" /></span>
                    <strong>Todo al día.</strong>
                    <span>No queda ninguna reserva por completar. Cuando una termine sin llegada registrada, aparecerá aquí y en la campana.</span>
                </div>
            )}

            {datos.fallidos.length > 0 && (
                <section className="rv-rs-grupo" aria-label="Cobros de no-show sin cobrar">
                    <div className="rv-rs-grupo__cab">
                        <span className="rv-rs-ceja">Cobros de no-show sin cobrar</span>
                        <span>{`${String(datos.fallidos.length)} ${datos.fallidos.length === 1 ? 'cobro' : 'cobros'}`}</span>
                    </div>
                    {datos.fallidos.map((f) => {
                        const nombre = nombreDeFallido(f);
                        const r = f.reserva;
                        return (
                            <div key={f.pago.id} className={`rv-rs-pend${movil ? ' rv-rs-pend--movil' : ''}`}>
                                <button type="button" className="rv-rs-fila__abrir" aria-label={`Abrir la reserva de ${nombre}`} onClick={() => { if (r !== null) { abrir(r.id); } }}>
                                    <span className="rv-rs-baldosa rv-rs-baldosa--roja"><Icono nombre="credit-card" /></span>
                                    <span className="rv-rs-fila__texto">
                                        <span className="rv-rs-fallido__cab"><strong>{nombre}</strong><strong className="rv-rs-importe">{dinero(f.pago.amount)}</strong></span>
                                        {r !== null && <span>{`No-show del ${diaAbreviado(r.service_date)}, ${r.local_time ?? horaEn(r.starts_at, contexto.zona)} · ${String(r.party_size)} personas · ${nombreZona(contexto, r.zone_id)}`}</span>}
                                        <span className="rv-rs-chips">
                                            <Chip estado="problem">Rechazado</Chip>
                                            <span className="rv-rs-intento">{`Intento ${String(f.intento)} de ${String(INTENTOS_COBRO)} · ${f.motivo ?? 'El banco lo rechazó'}`}</span>
                                        </span>
                                    </span>
                                </button>
                                <div className="rv-rs-pend__acciones">
                                    {puedeCobrar && !soloLectura && f.intento < INTENTOS_COBRO && (
                                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setVentana({ tipo: 'reintentar', f }); }}>
                                            <Icono nombre="refresh-cw" />
                                            <span>Reintentar el cobro</span>
                                        </button>
                                    )}
                                    <button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(`/pagos?apartado=pagos&pago=${encodeURIComponent(f.pago.id)}`); }}>
                                        <Icono nombre="arrow-right" />
                                        <span>Ver en Pagos</span>
                                    </button>
                                    {puedeDeshacer && !soloLectura && r !== null && (
                                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setVentana({ tipo: 'deshacer', r, fallido: true, terminada: new Date(r.ends_at).getTime() <= Date.now(), motivo: '', probado: false }); }}>
                                            <Icono nombre="undo-2" />
                                            <span>Deshacer el no-show</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {!puedeCobrar && (
                        <div className="rv-rs-nota">
                            <Icono nombre="lock" />
                            <span>Reintentar el cobro lo hace el propietario o un administrador.</span>
                        </div>
                    )}
                </section>
            )}

            {datos.noPresentadas.length > 0 && (
                <section className="rv-rs-grupo" aria-label="No presentadas estos días">
                    <div className="rv-rs-grupo__cab">
                        <span className="rv-rs-ceja">No presentadas estos días</span>
                        <span>Si alguna fue un error, deshazla.</span>
                    </div>
                    <div className="rv-rs-lista-blanca" role="list">
                        {datos.noPresentadas.map((r) => {
                            const nombre = nombreDe(r, nombres);
                            return (
                                <div key={r.id} role="listitem" className="rv-rs-ns">
                                    <button type="button" className="rv-rs-ns__abrir" onClick={() => { abrir(r.id); }}>
                                        <strong>{nombre}</strong>
                                        <span>{[`${etiquetaDia(r.service_date, hoy)}, ${r.local_time ?? horaEn(r.starts_at, contexto.zona)}`, `${String(r.party_size)} personas`, r.no_show_at == null ? '' : `marcada ${cuandoFue(r.no_show_at, contexto.zona, hoy)}`].filter((x) => x !== '').join(' · ')}</span>
                                    </button>
                                    <Chip estado="problem">No presentada</Chip>
                                    {!soloLectura && (
                                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setVentana({ tipo: 'deshacer', r, fallido: false, terminada: new Date(r.ends_at).getTime() <= Date.now(), motivo: '', probado: false }); }}>
                                            <Icono nombre="undo-2" />
                                            <span>Deshacer el no-show</span>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            <Ventana
                abierta={ventana !== null}
                titulo={textoVentana.titulo}
                texto={textoVentana.texto}
                accion={textoVentana.accion}
                accionIcono={textoVentana.icono}
                ocupado={ocupado}
                ocupadoTexto={textoVentana.ocupadoTexto}
                movil={movil}
                onAccion={confirmarVentana}
                onCerrar={() => { if (!ocupado) { setVentana(null); } }}
            >
                {ventana?.tipo === 'no-vino' && (
                    <Interruptor etiqueta="Cobrar el no-show" ariaLabel="Cobrar el no-show" ayuda="Si no lo cobras, queda en Auditoría." valor={ventana.cobrar} onChange={(v) => { setVentana({ ...ventana, cobrar: v }); }} linea="ninguna" />
                )}
                {ventana?.tipo === 'deshacer' && (
                    <Campo
                        etiqueta="Motivo"
                        valor={ventana.motivo}
                        onChange={(v) => { setVentana({ ...ventana, motivo: v }); }}
                        error={ventana.probado && ventana.motivo.trim().length < MOTIVO_MIN ? 'Escribe el motivo.' : null}
                        max={500}
                        ayuda="Por ejemplo: llegó tarde y se sentó."
                        nombre="motivo-deshacer"
                        filas={2}
                    />
                )}
            </Ventana>
        </div>
    );
}
